// Camera + QR decoding (spec §8.3 step 1).
//
// jsQR against canvas frames, as the tech spec names. The native
// BarcodeDetector would be faster but its availability across Android WebViews
// is uneven, and the fallback would be jsQR anyway — so shipping only jsQR
// removes a branch and makes gate behaviour identical on every device (R4).
import jsQR from 'jsqr';

/** Long edge of the decode buffer. Full sensor resolution is the main way to
 *  spend the 2-second budget on a modest Android (R4). */
const DECODE_EDGE = 640;

/** How long an identical decode is ignored before it can fire again (R11). */
const REPEAT_COOLDOWN_MS = 3000;

/**
 * Consecutive undecoded frames before the memo of the last code is dropped.
 *
 * jsQR fails on individual frames all the time — a hand tremor, a reflection,
 * one blurred frame while autofocus hunts. Forgetting the last code on the
 * first miss would let a ticket still resting in front of the lens fire again
 * immediately, which is the duplicate storm the cooldown exists to prevent. A
 * code genuinely taken away misses every frame, so it clears in a third of a
 * second regardless.
 */
const MISSES_TO_FORGET = 10;

export class Scanner {
  constructor(video, onDecode) {
    this.video = video;
    this.onDecode = onDecode;
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.stream = null;
    this.raf = null;
    this.lastCode = null;
    this.lastAt = 0;
    this.missCount = 0;
  }

  async start() {
    // getUserMedia requires a secure context. On a phone that means localhost
    // (via Chrome DevTools port forwarding) or HTTPS — a LAN IP will fail here
    // silently-looking, which is why the caller surfaces this error (R6).
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
      audio: false,
    });
    this.video.srcObject = this.stream;
    this.video.setAttribute('playsinline', 'true');
    try {
      await this.video.play();
    } catch (err) {
      // The stream is already live at this point. Leaving it running would keep
      // the camera lit and the privacy light on for the rest of the event while
      // the app shows its "No camera" screen — a hot phone and a flat battery
      // by mid-afternoon.
      this.stop();
      throw err;
    }
    this.loop();
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    this.video.srcObject = null;
    // Battery and heat matter across a whole event: the camera must not run
    // while the app is on another screen.
  }

  loop = () => {
    this.raf = requestAnimationFrame(this.loop);
    const { video } = this;
    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;

    const scale = DECODE_EDGE / Math.max(video.videoWidth, video.videoHeight);
    const w = Math.round(video.videoWidth * Math.min(1, scale));
    const h = Math.round(video.videoHeight * Math.min(1, scale));
    if (w === 0 || h === 0) return;

    this.canvas.width = w;
    this.canvas.height = h;
    this.ctx.drawImage(video, 0, 0, w, h);
    const image = this.ctx.getImageData(0, 0, w, h);
    const result = jsQR(image.data, w, h, { inversionAttempts: 'dontInvert' });

    if (!result) {
      // Only a RUN of misses means the code left the frame. Clearing the memo
      // then is what lets a DELIBERATE re-scan of the same ticket still
      // register — that is US1 scenario 2, and the debounce must not suppress
      // it — while a single dropped frame no longer counts as departure.
      if (++this.missCount >= MISSES_TO_FORGET) this.lastCode = null;
      return;
    }
    this.missCount = 0;

    const now = Date.now();
    if (result.data === this.lastCode && now - this.lastAt < REPEAT_COOLDOWN_MS) {
      // A QR resting in front of the lens decodes dozens of times a second.
      // Without this, one delegate becomes a stream of duplicate warnings and
      // the outbox fills with junk (FR-034).
      return;
    }
    this.lastCode = result.data;
    this.lastAt = now;
    this.onDecode(result.data);
  };
}
